import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { ServiceBadge } from "@/components/dashboard/ServiceBadge";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { ClientCombobox } from "@/components/shared/ClientCombobox";
import { clientService } from "@/services/client";
import {
  subscriptionService,
  ESubscriptionStatus,
  ESubscriptionType,
  ESubType,
  SubscriptionFilters,
  getSubscriptionServiceId,
  getSubscriptionStreamingAccount,
  EownerType,
} from "@/services/subscription";
import { streamingServiceApi } from "@/services/streaming-service";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Eye, Loader2, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { CreateSubscriptionModal } from "@/components/subscriptions/CreateSubscriptionModal";
import { formatCurrency } from "@/lib/utils";
import { useUrlFilters, useUrlPage } from "@/hooks/use-url-filters";

const PAGE_SIZE = 10;

const DEFAULT_FILTERS = {
  clientId: "",
  service: "ALL",
  status: ESubscriptionStatus.ACTIVE as string,
  subType: "ALL",
  type: "ALL",
};

function generatePaginationNumbers(
  currentPage: number,
  totalPages: number
): (number | string)[] {
  const delta = 1;
  const range: (number | string)[] = [];

  for (let i = 1; i <= totalPages; i++) {
    if (
      i === 1 ||
      i === totalPages ||
      (i >= currentPage - delta && i <= currentPage + delta)
    ) {
      range.push(i);
    } else if (range[range.length - 1] !== "...") {
      range.push("...");
    }
  }

  return range;
}

export default function Subscriptions() {
  const navigate = useNavigate();

  const [urlFilters, setUrlFilters] = useUrlFilters(DEFAULT_FILTERS);
  const {
    clientId,
    service: serviceFilter,
    status: statusFilter,
    subType: subTypeFilter,
    type: typeFilter,
  } = urlFilters;
  const [currentPage, setCurrentPage] = useUrlPage();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Hidratar el cliente seleccionado a partir del clientId en la URL
  const { data: selectedClientResponse } = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => clientService.getOne(clientId),
    enabled: !!clientId,
  });
  const selectedClient = clientId ? selectedClientResponse?.data ?? null : null;

  // Fetch streaming services for the filter dropdown
  const { data: servicesResponse } = useQuery({
    queryKey: ["streaming-services"],
    queryFn: () => streamingServiceApi.getAll(),
    staleTime: 5 * 60 * 1000,
  });
  const services = servicesResponse?.data || [];

  // Build filters
  const filters: SubscriptionFilters = {
    page: currentPage,
    limit: PAGE_SIZE,
    ownerType: EownerType.CLIENT,
    clientId: clientId ? Number(clientId) : undefined,
    serviceId: serviceFilter !== "ALL" ? Number(serviceFilter) : undefined,
    status:
      statusFilter !== "ALL"
        ? (statusFilter as ESubscriptionStatus)
        : undefined,
    type:
      subTypeFilter !== "ALL" ? (subTypeFilter as ESubType) : undefined,
    subscriptionType:
      typeFilter !== "ALL" ? (typeFilter as ESubscriptionType) : undefined,
  };

  const {
    data: subscriptionsResponse,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: [
      "subscriptions",
      currentPage,
      clientId,
      serviceFilter,
      statusFilter,
      subTypeFilter,
      typeFilter,
    ],
    queryFn: () => subscriptionService.getAll(filters),
  });

  const subscriptions = subscriptionsResponse?.data || [];
  const pagination = subscriptionsResponse?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const totalItems = pagination?.totalItems || 0;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("es-CO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <>
    <DashboardLayout
      title="Suscripciones"
      subtitle="Gestiona todas las suscripciones"
    >
      <div className="space-y-6 animate-fade-in">
        {/* Filters Bar + Botón */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px] max-w-xs">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Cliente
            </label>
            <ClientCombobox
              value={selectedClient}
              onChange={(client) => setUrlFilters({ clientId: client?.id ?? "" })}
            />
          </div>

          <div className="w-full sm:w-[160px]">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Servicio
            </label>
            <Select value={serviceFilter} onValueChange={(value) => setUrlFilters({ service: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos los servicios</SelectItem>
                {services.map((s) => (
                  <SelectItem key={s.id} value={s.id.toString()}>
                    {s.icon} {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-[150px]">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Estado
            </label>
            <Select value={statusFilter} onValueChange={(value) => setUrlFilters({ status: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos los estados</SelectItem>
                <SelectItem value={ESubscriptionStatus.ACTIVE}>Activo</SelectItem>
                <SelectItem value={ESubscriptionStatus.EXPIRING}>Por vencer</SelectItem>
                <SelectItem value={ESubscriptionStatus.EXPIRED}>Vencido</SelectItem>
                <SelectItem value={ESubscriptionStatus.CANCELLED}>Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-[140px]">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Tipo
            </label>
            <Select value={subTypeFilter} onValueChange={(value) => setUrlFilters({ subType: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos</SelectItem>
                <SelectItem value={ESubType.SINGLE}>Individual</SelectItem>
                <SelectItem value={ESubType.COMBO}>Combo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="w-full sm:w-[150px]">
            <label className="text-sm font-medium text-muted-foreground mb-1 block">
              Subtipo
            </label>
            <Select value={typeFilter} onValueChange={(value) => setUrlFilters({ type: value })}>
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos</SelectItem>
                <SelectItem value={ESubscriptionType.SLOT}>Slot</SelectItem>
                <SelectItem value={ESubscriptionType.FULL_ACCOUNT}>
                  Cuenta completa
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="ml-auto">
            <Button className="gap-2 h-10" onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="h-4 w-4" />
              Nueva Suscripción
            </Button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>
              Error al cargar las suscripciones. Por favor, intenta de nuevo.
            </AlertDescription>
          </Alert>
        )}

        {/* Subscriptions Table */}
        <div className="card-elevated overflow-hidden relative">
          {isFetching && !isLoading && (
            <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {isLoading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-muted-foreground mt-2">
                Cargando suscripciones...
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="font-semibold">Cliente</TableHead>
                  <TableHead className="font-semibold">Servicio</TableHead>
                  <TableHead className="font-semibold">Slot</TableHead>
                  <TableHead className="font-semibold text-right">
                    Precio
                  </TableHead>
                  <TableHead className="font-semibold">Inicio</TableHead>
                  <TableHead className="font-semibold">Fin</TableHead>
                  <TableHead className="font-semibold text-center">
                    Dias rest.
                  </TableHead>
                  <TableHead className="font-semibold">Estado</TableHead>
                  <TableHead className="font-semibold text-right">
                    Acciones
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscriptions.map((sub) => (
                  <TableRow
                    key={sub.id}
                    className="transition-colors hover:bg-muted/30"
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="text-primary font-medium text-xs">
                            {sub?.client.name
                              ?.split(" ")
                              .map((n) => n[0])
                              .join("") || "?"}
                          </span>
                        </div>
                        <span className="font-medium text-foreground">
                          {sub?.client.name|| "—"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {sub.type === ESubType.COMBO ? (
                        <ServiceBadge icon="📦" name={sub.combo.name || "Combo"} />
                      ) : (
                        <ServiceBadge
                          icon={sub.subscriptionItems[0]?.service.icon || ""}
                          name={sub.subscriptionItems[0]?.service.name || ""}
                        />
                      ) }
                    </TableCell>
                    <TableCell>
                      <span className="text-muted-foreground">
                        {sub.type === ESubType.SINGLE
                                            ? sub.subscriptionItems[0].subscriptionType === ESubscriptionType.SLOT? sub.subscriptionItems[0].slot.name
                                            : "Cuenta completa" :'Combo'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      ${formatCurrency(sub.price)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(sub.startDate)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(sub.endDate)}
                    </TableCell>
                    <TableCell className="text-center">
                      <span
                        className={
                          sub.daysRemaining <= 3
                            ? "text-destructive font-semibold"
                            : sub.daysRemaining <= 7
                            ? "text-yellow-600 font-medium"
                            : "text-muted-foreground"
                        }
                      >
                        {sub.daysRemaining}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={sub.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          navigate(`/subscriptions/${sub.id}`)
                        }
                      >
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {!isLoading && subscriptions.length === 0 && (
            <div className="p-8 text-center">
              <p className="text-muted-foreground">
                No se encontraron suscripciones
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                Intenta ajustar los filtros
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4">
            <p className="text-sm text-muted-foreground">
              Mostrando {subscriptions.length} de {totalItems} suscripciones
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage(Math.max(1, currentPage - 1))
                }
                disabled={currentPage === 1 || isFetching}
              >
                <ChevronLeft className="h-4 w-4" />
                Anterior
              </Button>
              <div className="flex items-center gap-1">
                {generatePaginationNumbers(currentPage, totalPages).map(
                  (page, idx) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-2 text-muted-foreground"
                      >
                        ...
                      </span>
                    ) : (
                      <Button
                        key={page}
                        variant={
                          currentPage === page ? "default" : "outline"
                        }
                        size="sm"
                        className="w-9"
                        onClick={() => setCurrentPage(page as number)}
                        disabled={isFetching}
                      >
                        {page}
                      </Button>
                    )
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages || isFetching}
              >
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>

      <CreateSubscriptionModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
      />
    </>
  );
}
